import { useCallback, useMemo, useState } from "react";
import {
  NonCancelableCustomEvent,
  TableProps,
} from "@amzn/awsui-components-react";
import { DLQItem } from "../lusca-search-api/generated-src";

export function useDlqTable(dlqItems: DLQItem[]) {
  const [selectedItem, setSelectedItem] = useState<DLQItem | null>(null);
  const [sortingColumn, setSortingColumn] = useState<
    TableProps.SortingColumn<DLQItem>
  >({
    sortingField: "timestamp",
  });
  const [sortingDescending, setSortingDescending] = useState(true);
  const [filteringText, setFilteringText] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize] = useState(20);

  const queueSummary = useMemo(() => {
    const summary: { [key: string]: number } = {};
    dlqItems.forEach((item) => {
      summary[item.eventsourcekey] = (summary[item.eventsourcekey] || 0) + 1;
    });
    return summary;
  }, [dlqItems]);

  const filteredItems = useMemo(
    () =>
      dlqItems.filter((item) => {
        const searchText = filteringText.toLowerCase();
        const messageString = item.is_malformed_json
          ? (item.processed_body as string)
          : JSON.stringify(item.processed_body);

        return (
          Object.entries(item)
            .filter(([key]) => key !== "message")
            .some(([, value]) =>
              String(value).toLowerCase().includes(searchText)
            ) || messageString.toLowerCase().includes(searchText)
        );
      }),
    [dlqItems, filteringText]
  );

  const sortedItems = useMemo(
    () =>
      [...filteredItems].sort((a, b) => {
        const key = sortingColumn.sortingField as keyof DLQItem;
        const aVal = a[key];
        const bVal = b[key];
        // undefined last
        if (aVal === undefined && bVal === undefined) return 0;
        if (aVal === undefined) return 1;
        if (bVal === undefined) return -1;
        if (aVal < bVal) return sortingDescending ? 1 : -1;
        if (aVal > bVal) return sortingDescending ? -1 : 1;
        return 0;
      }),
    [filteredItems, sortingColumn, sortingDescending]
  );

  const paginatedItems = useMemo(
    () =>
      sortedItems.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
      ),
    [sortedItems, currentPageIndex, pageSize]
  );

  const handleSortingChange = useCallback(
    (event: NonCancelableCustomEvent<TableProps.SortingState<DLQItem>>) => {
      setSortingColumn(event.detail.sortingColumn);
      setSortingDescending(event.detail.isDescending ?? false);
    },
    []
  );

  return {
    selectedItem,
    setSelectedItem,
    sortingColumn,
    sortingDescending,
    filteringText,
    setFilteringText,
    currentPageIndex,
    setCurrentPageIndex,
    pageSize,
    queueSummary,
    filteredItems,
    paginatedItems,
    handleSortingChange,
  };
}
